import * as React from 'react';
import { Suspense, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from 'styled-components';

import { useLocation } from '@reach/router';

import ContactUsForm from '../components/contact-us-form/ContactUsForm';
import { CookieBanner } from '../components/cookie-banner';
import { Footer } from '../components/footer';
import { CONTACT_US_FORMS } from '../components/footer/helpers';
import { Header } from '../components/header';
import Modal from '../components/modal/index';
import { createOrganizationSchema } from '../components/Seo/OrganizationSchema';
import { ScrollTopButton } from '../components/scroll-top-button';
import Spinner from '../components/spinner';
import { useSiteMetadata } from '../hooks/use-site-metadata';
import ScrollToTop from '../hooks/use-scroll-to-top';
import { ModalContext } from '../contexts/ModalContext';
import { defaultTheme } from '../styles/themes';

import { GlobalLayoutStyle, ScMainContainer } from './styled';

import '../styles/themes/fonts.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface MainLayoutProps {
	children: React.ReactNode;
}

const pagesWithoutSiteChrome = ['/nursery-savings-calculator/'];

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
	const location = useLocation();
	const { siteUrl } = useSiteMetadata();
	const organizationSchema = createOrganizationSchema(siteUrl);
	const hideSiteChrome = pagesWithoutSiteChrome.includes(location.pathname);
	const isNurserySavingsCalculator = location.pathname === '/nursery-savings-calculator/';
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [modalType, setModalType] = useState('');

	const handleOpenModal = (modalType: string) => {
		setIsOpenModal(true);
		setModalType(modalType);
	};

	useEffect(() => {
		const noscriptElement = document.createElement('noscript');
		const iframeElement = document.createElement('iframe');
		iframeElement.src = `https://www.googletagmanager.com/ns.html?id=GTM-WD7QQM8`;
		iframeElement.height = '0';
		iframeElement.width = '0';
		iframeElement.style.display = 'none';
		iframeElement.style.visibility = 'hidden';
		noscriptElement.appendChild(iframeElement);
		const bodyElement = document.getElementsByTagName('body')[0];
		bodyElement.insertBefore(noscriptElement, bodyElement.firstChild);
	}, []);

	return (
		<ModalContext.Provider value={{ openModal: handleOpenModal }}>
			<ThemeProvider theme={defaultTheme}>
				<GlobalLayoutStyle />
				<ScMainContainer
					isWhite={
						location.pathname.includes('tell') ||
						location.pathname.includes('plans') ||
						location.pathname.includes('get-started')
					}
				>
					<Helmet
						htmlAttributes={{
							lang: 'en',
						}}
					>
						{isNurserySavingsCalculator && <meta name="robots" content="noindex, nofollow" />}
						{isNurserySavingsCalculator && <meta name="googlebot" content="noindex, nofollow" />}
						<script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-WD7QQM8');`}</script>
						<script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
					</Helmet>

					{modalType && isOpenModal && (
						<Modal
							open={isOpenModal}
							title={'Contact us'}
							handleClose={() => {
								setIsOpenModal(false);
								setModalType('');
							}}
						>
							<ContactUsForm modalType={modalType as CONTACT_US_FORMS} />
						</Modal>
					)}
					<ScrollToTop />
					{!hideSiteChrome && <Header openModal={handleOpenModal} />}
					<Suspense fallback={<Spinner />}>{children}</Suspense>
					{!hideSiteChrome && <Footer openModal={handleOpenModal} />}
					{!hideSiteChrome && <ScrollTopButton />}
					{!hideSiteChrome && <CookieBanner />}
				</ScMainContainer>
			</ThemeProvider>
		</ModalContext.Provider>
	);
};
